/**
 * /dashboard/accounting/reports — Reports hub (G_ACC8).
 *
 * Cards link to the four statutory financial statements + the
 * dashboard hero widget preview.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/page.tsx.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BarChart3,
  ClipboardList,
  Landmark,
  ShieldAlert,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPaise } from "@/lib/expense-format";
import { useAccountingDashboardWidget } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/reports/")({
  component: ReportsHubRoute,
});

interface ReportTile {
  label: string;
  href: string;
  icon: LucideIcon;
  blurb: string;
}

const REPORTS: readonly ReportTile[] = [
  {
    label: "Trial Balance",
    href: "/app/$tenantSlug/accounting/reports/trial-balance",
    icon: ClipboardList,
    blurb:
      "Every account's debit / credit position as of a date. Must balance per Companies Act §128(1).",
  },
  {
    label: "Profit and Loss",
    href: "/app/$tenantSlug/accounting/reports/profit-and-loss",
    icon: TrendingUp,
    blurb:
      "Revenue, expenses and net profit for a period. Schedule III Part II grouping.",
  },
  {
    label: "Balance Sheet",
    href: "/app/$tenantSlug/accounting/reports/balance-sheet",
    icon: Landmark,
    blurb:
      "Assets vs Equity + Liabilities snapshot. Schedule III Part I ordering.",
  },
  {
    label: "MSME Overdue",
    href: "/app/$tenantSlug/accounting/reports/msme-overdue",
    icon: ShieldAlert,
    blurb:
      "Bills owed to MSME-registered suppliers past the MSMED Act §15 45-day clock.",
  },
] as const;

function DashboardPreview() {
  const query = useAccountingDashboardWidget();
  const widget = query.data ?? null;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  if (error || !widget) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Dashboard widget unavailable — {error ?? "no data"}.
        </CardContent>
      </Card>
    );
  }

  const tiles = [
    {
      label: "Cash on hand",
      value: formatPaise(widget.cash_on_hand_minor),
      icon: Wallet,
      sub: "Active bank/cash/UPI wallets",
    },
    {
      label: "Receivable",
      value: formatPaise(widget.accounts_receivable_minor),
      icon: TrendingUp,
      sub: "Unpaid invoices (sent + partly paid)",
    },
    {
      label: "Payable",
      value: formatPaise(widget.accounts_payable_minor),
      icon: BarChart3,
      sub: "Unpaid bills (received + partly paid)",
    },
    {
      label: "Last 30d profit",
      value: formatPaise(widget.profit_30d_minor),
      icon: TrendingUp,
      sub: `${widget.profit_30d_period_start} → ${widget.profit_30d_period_end}`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Card key={t.label}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardDescription>{t.label}</CardDescription>
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold tracking-tight">
                  {t.value}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{t.sub}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-3">
          <div className="text-sm">
            <span className="text-muted-foreground">MSME overdue</span>
            <p className="text-base font-medium">
              {widget.msme_overdue_count}{" "}
              {widget.msme_overdue_count === 1 ? "bill" : "bills"} —{" "}
              {formatPaise(widget.msme_overdue_outstanding_minor)} outstanding
            </p>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Unreconciled receipts</span>
            <p className="text-base font-medium">
              {widget.unreconciled_receipts_count} draft
            </p>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">As of</span>
            <p className="text-base font-medium">{widget.as_of}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ReportsHubRoute() {
  return (
    <AccountingModuleShell
      title="Reports"
      description="Statutory financial views from your books-of-account, computed live."
    >
      <DashboardPreview />
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <Link key={r.href} to={r.href} className="group">
              <Card className="h-full transition-colors group-hover:border-primary/40">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                  <CardTitle className="mt-3 text-base">{r.label}</CardTitle>
                  <CardDescription>{r.blurb}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </AccountingModuleShell>
  );
}
